import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "size", "onClick", "className", "onMouseDown", "onMouseUp", "jss", "isLoading", "disabled", "spinnerColor", "enableRipple", "rippleColor", "component", "type"];
import React, { useCallback, useRef } from 'react';
import cn from 'classnames';
import createStyles from "../createStyles";
import Spinner from "../Spinner";
import { capitalize } from "../utils/commonUtility";
import TouchRipple from "./TouchRipple";
import { gray400, white } from "../theme/colors";
export var useStyles = createStyles(function (theme) {
  return {
    root: {
      border: 0,
      padding: 0,
      cursor: 'pointer',
      position: 'relative',
      outline: 'none',
      borderRadius: 3,
      transition: theme.transition.create(['background-color', 'box-shadow', 'border']),
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    content: {
      display: 'flex',
      height: '100%',
      width: '100%',
      zIndex: 1,
      justifyContent: 'inherit',
      alignItems: 'inherit'
    },
    cursorDefault: {
      cursor: 'default !important',
      pointerEvents: 'none'
    },
    disabled: {
      cursor: 'not-allowed !important'
    },
    pointerEventsNone: {
      pointerEvents: 'none'
    },
    spinner: {
      position: 'absolute'
    },
    hidden: {
      visibility: 'hidden'
    },
    btnMd: {
      minHeight: 36,
      fontWeight: 'bold',
      padding: '8px 15px'
    },
    btnLg: {
      minHeight: 46,
      padding: '8px 15px',
      fontSize: theme.typography.fontSize.h3
    },
    btnXl: {
      minHeight: 54,
      padding: "0 ".concat(theme.spacer(4), "px"),
      fontSize: theme.typography.fontSize.h3
    },
    webkitAppearanceNone: {
      appearance: 'none'
    }
  };
}, {
  name: 'Button'
});
var Button = /*#__PURE__*/React.forwardRef(function Button(_ref, ref) {
  var _cn;

  var children = _ref.children,
      size = _ref.size,
      onClick = _ref.onClick,
      className = _ref.className,
      onMouseDown = _ref.onMouseDown,
      onMouseUp = _ref.onMouseUp,
      jss = _ref.jss,
      _ref$isLoading = _ref.isLoading,
      isLoading = _ref$isLoading === void 0 ? false : _ref$isLoading,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$spinnerColor = _ref.spinnerColor,
      spinnerColor = _ref$spinnerColor === void 0 ? white : _ref$spinnerColor,
      _ref$enableRipple = _ref.enableRipple,
      enableRipple = _ref$enableRipple === void 0 ? true : _ref$enableRipple,
      _ref$rippleColor = _ref.rippleColor,
      rippleColor = _ref$rippleColor === void 0 ? gray400 : _ref$rippleColor,
      _ref$component = _ref.component,
      component = _ref$component === void 0 ? 'button' : _ref$component,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'button' : _ref$type,
      props = _objectWithoutProperties(_ref, _excluded);

  var buttonProps = props;
  var classes = useStyles({
    jss: jss
  });
  var rippleRef = useRef(null);
  var shouldEnableRipple = enableRipple && !isLoading && !disabled;
  var Component = component || 'button';
  var isButton = component === 'button';
  var handleMouseDown = useCallback(function (e) {
    if (onMouseDown) {
      onMouseDown(e);
    }

    var touchRipple = rippleRef.current;

    if (enableRipple && touchRipple) {
      touchRipple.ripple(e);
    }
  }, [enableRipple, onMouseDown, rippleRef]);
  var handleMouseUp = useCallback(function (e) {
    if (onMouseUp) {
      onMouseUp(e);
    }

    var touchRipple = rippleRef.current;

    if (enableRipple && touchRipple) {
      touchRipple.stop(e);
    }
  }, [enableRipple, onMouseUp, rippleRef]);
  return /*#__PURE__*/React.createElement(Component, _extends({}, buttonProps, {
    disabled: disabled,
    type: type,
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
    onClick: onClick,
    className: cn(classes.root, (_cn = {}, _defineProperty(_cn, classes.cursorDefault, isLoading), _defineProperty(_cn, classes.disabled, disabled), _defineProperty(_cn, classes.pointerEventsNone, disabled && !isButton), _defineProperty(_cn, classes.webkitAppearanceNone, !isButton), _defineProperty(_cn, classes["btn".concat(capitalize(size))], size), _cn), className),
    ref: ref
  }), isLoading && /*#__PURE__*/React.createElement(Spinner, {
    className: classes.spinner,
    size: 20,
    strokeWidth: 6,
    color: spinnerColor
  }), /*#__PURE__*/React.createElement("span", {
    className: cn(classes.content, _defineProperty({}, classes.hidden, isLoading))
  }, children), shouldEnableRipple && /*#__PURE__*/React.createElement(TouchRipple, {
    ref: rippleRef,
    color: rippleColor
  }));
});
Button.displayName = 'Button';
export default Button;